import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppState, Barcode, BarcodeCorrection, BarcodeLookup, BarcodeMiss, Can, Entry, Profile, PublicBarcodeLookup, PublicList, WantedItem } from '../shared/api';
import { fitsWanted } from '../shared/catalog';
import { normalizeEan } from '../shared/barcode';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...rest,
    headers: {
      ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const body = res.headers.get('content-type')?.includes('json') ? await res.json() : {};
  if (!res.ok) {
    throw new ApiError((body as { error?: string }).error ?? 'Er ging iets mis.', res.status, body);
  }
  return body as T;
}

export const STATE_KEY = ['state'] as const;

export function useAppState() {
  return useQuery({
    queryKey: STATE_KEY,
    queryFn: () => api<AppState>('/api/state'),
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
  });
}

/** Applies a change to the cached state, so the UI updates without a refetch. */
function useStatePatch() {
  const qc = useQueryClient();
  return (fn: (s: AppState) => AppState) =>
    qc.setQueryData<AppState>(STATE_KEY, (s) => (s ? fn(s) : s));
}

export function useAddEntry() {
  const patch = useStatePatch();
  return useMutation({
    mutationFn: (body: { canId: number; country: string; sizeMl: number; ean?: string }) =>
      api<Entry>('/api/entries', { method: 'POST', json: body }),
    onSuccess: (entry) =>
      patch((s) => {
        // A can in another country or size than the zoeklijst asks for stays wanted.
        const want = s.wanted.find((w) => w.canId === entry.canId);
        const found = !want || fitsWanted(want, entry);
        return {
          ...s,
          entries: [...s.entries, entry],
          wanted: found ? s.wanted.filter((w) => w.canId !== entry.canId) : s.wanted,
          reservations: found ? s.reservations.filter((r) => r.canId !== entry.canId) : s.reservations,
        };
      }),
  });
}

export function useUpdateEntry() {
  const patch = useStatePatch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; country?: string; sizeMl?: number }) =>
      api<Entry>(`/api/entries/${id}`, { method: 'PATCH', json: body }),
    // Optimistic: the chip lights up immediately.
    onMutate: ({ id, ...body }) =>
      patch((s) => ({ ...s, entries: s.entries.map((e) => (e.id === id ? { ...e, ...body } : e)) })),
    onSuccess: (entry) =>
      patch((s) => ({ ...s, entries: s.entries.map((e) => (e.id === entry.id ? entry : e)) })),
    onError: () => qc.invalidateQueries({ queryKey: STATE_KEY }),
  });
}

export function useDeleteEntry() {
  const patch = useStatePatch();
  return useMutation({
    mutationFn: (id: number) => api(`/api/entries/${id}`, { method: 'DELETE' }),
    onSuccess: (_r, id) => patch((s) => ({ ...s, entries: s.entries.filter((e) => e.id !== id) })),
  });
}

function upload(path: string, file: File) {
  const form = new FormData();
  form.append('file', file);
  return api<{ imageId: string }>(path, { method: 'POST', body: form });
}

export function useUploadCanImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => upload(`/api/cans/${id}/image`, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: STATE_KEY }),
  });
}

export function useAddCan() {
  const patch = useStatePatch();
  return useMutation({
    mutationFn: (body: { name: string; categoryId: string; wanted?: boolean }) =>
      api<Can>('/api/cans', { method: 'POST', json: body }),
    onSuccess: (can, body) =>
      patch((s) => ({ ...s, cans: [...s.cans, can], wanted: body.wanted ? [...s.wanted, anyVariant(can.id)] : s.wanted })),
  });
}

const anyVariant = (canId: number): WantedItem => ({ canId, country: null, sizeMl: null });

/** Puts a flavor on the zoeklijst, or takes it off. Optimistic. */
export function useToggleWanted() {
  const patch = useStatePatch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ canId, wanted, preference }: { canId: number; wanted: boolean; preference?: WantedItem }) =>
      wanted
        ? api('/api/wanted', { method: 'POST', json: preference ?? { canId } })
        : api(`/api/wanted/${canId}`, { method: 'DELETE' }),
    onMutate: ({ canId, wanted, preference }) =>
      patch((s) => ({
        ...s,
        wanted: wanted
          ? [...s.wanted.filter((w) => w.canId !== canId), preference ?? anyVariant(canId)]
          : s.wanted.filter((w) => w.canId !== canId),
        reservations: wanted ? s.reservations : s.reservations.filter((r) => r.canId !== canId),
      })),
    onError: () => qc.invalidateQueries({ queryKey: STATE_KEY }),
  });
}

/** Which country and size of a flavor on the zoeklijst is looked for; null is any. Optimistic. */
export function useSetWantedPreference() {
  const patch = useStatePatch();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (item: WantedItem) => api('/api/wanted', { method: 'POST', json: item }),
    onMutate: (item) => patch((s) => ({ ...s, wanted: s.wanted.map((w) => (w.canId === item.canId ? item : w)) })),
    onError: () => qc.invalidateQueries({ queryKey: STATE_KEY }),
  });
}

export const BARCODES_KEY = ['barcodes'] as const;

export function useBarcodes() {
  return useQuery({ queryKey: BARCODES_KEY, queryFn: () => api<Barcode[]>('/api/barcodes') });
}

export function useDeleteBarcode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ean: string) => api(`/api/barcodes/${encodeURIComponent(ean)}`, { method: 'DELETE' }),
    onSuccess: (_r, ean) => qc.setQueryData<Barcode[]>(BARCODES_KEY, (list) => list?.filter((b) => b.ean !== ean)),
  });
}

/** Returns the saved mapping immediately and refreshes all barcode-derived queries. */
export function useCorrectBarcode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ean, ...body }: BarcodeCorrection & { ean: string }) =>
      api<Barcode>(`/api/barcodes/${encodeURIComponent(normalizeEan(ean))}`, { method: 'PUT', json: body }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: BARCODES_KEY }),
        qc.invalidateQueries({ queryKey: ['barcode-lookup'] }),
        qc.invalidateQueries({ queryKey: ['public'] }),
      ]);
    },
  });
}

export function useUpdateProfile() {
  const patch = useStatePatch();
  return useMutation({
    mutationFn: (body: Partial<Omit<Profile, 'username'>>) =>
      api<Profile>('/api/profile', { method: 'PATCH', json: body }),
    onMutate: (body) => patch((s) => ({ ...s, profile: { ...s.profile, ...body } })),
    onSuccess: (profile) => patch((s) => ({ ...s, profile })),
  });
}

/**
 * A known EAN gives its flavor; an unknown one gives what Open Food Facts
 * calls the product (if anything), to start the name search with.
 */
export async function lookupBarcode(
  ean: string,
): Promise<{ hit: BarcodeLookup; suggestion?: undefined } | { hit: null; suggestion: string | null }> {
  try {
    return { hit: await api<BarcodeLookup>(`/api/barcodes/${encodeURIComponent(ean)}`) };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { hit: null, suggestion: (err.body as Partial<BarcodeMiss>).suggestion ?? null };
    }
    throw err;
  }
}

// --- public page -------------------------------------------------------------

/** Always performs a fresh, read-only lookup. 404 codes distinguish missing list/code. */
export function lookupPublicBarcode(slug: string, ean: string, signal?: AbortSignal): Promise<PublicBarcodeLookup> {
  return api<PublicBarcodeLookup>(
    `/api/public/${encodeURIComponent(slug)}/barcodes/${encodeURIComponent(normalizeEan(ean))}`,
    { signal, headers: { 'x-friend-token': token() } },
  );
}

export function usePublicBarcodeLookup(slug: string) {
  return useMutation({ mutationFn: (ean: string) => lookupPublicBarcode(slug, ean) });
}

const TOKEN_KEY = 'monsters.friendToken';
const NAME_KEY = 'monsters.friendName';

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** A random id this browser keeps, so a friend can undo their own claim. */
export function friendToken(): string {
  const s = storage();
  let token = s?.getItem(TOKEN_KEY) ?? null;
  if (!token) {
    const bytes = crypto.getRandomValues(new Uint8Array(18));
    token = btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, (c) => (c === '+' ? '-' : c === '/' ? '_' : ''));
    try {
      s?.setItem(TOKEN_KEY, token);
    } catch {
      /* private mode: the claim still works for this visit */
    }
  }
  return token;
}

export function getFriendName(): string {
  return storage()?.getItem(NAME_KEY) ?? '';
}

export function setFriendName(name: string) {
  try {
    storage()?.setItem(NAME_KEY, name);
  } catch {
    /* ignore */
  }
}

let memoryToken: string | null = null;
const token = () => (memoryToken ??= friendToken());

export function usePublicList(slug: string) {
  return useQuery({
    queryKey: ['public', slug],
    queryFn: () => api<PublicList>(`/api/public/${slug}`, { headers: { 'x-friend-token': token() } }),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
}

export function useToggleReservation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ canId, reserve, friendName }: { canId: number; reserve: boolean; friendName: string }) =>
      reserve
        ? api(`/api/public/${slug}/reservations`, {
            method: 'POST',
            json: { canId, friendName },
            headers: { 'x-friend-token': token() },
          })
        : api(`/api/public/${slug}/reservations/${canId}`, {
            method: 'DELETE',
            headers: { 'x-friend-token': token() },
          }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['public', slug] }),
  });
}
